// Multi-file attachment picker shared by the new-case modal and the
// "I'm seeing this too" incident form. Holds a list of picked files (with
// image previews), enforces QC_MAX_ATTACHMENTS, and lets the user remove any
// one before submitting. The parent owns the list (it needs the File[] to
// upload on submit); this component renders the input + thumbnail strip and
// exposes an `appendFiles` helper so callers can also feed it pasted images.

import { useEffect, useRef } from 'react';
import { QC_MAX_ATTACHMENTS } from '@itms-qc/shared';
import { labelStyle, secondaryButtonStyle } from './form';

export interface PickedFile {
  file: File;
  previewUrl: string | null; // object URL for images, null otherwise
  key: string; // stable React key
}

let seq = 0;

/** Wrap raw Files as PickedFiles, minting preview URLs for images. */
export function toPickedFiles(files: File[]): PickedFile[] {
  return files.map((file) => ({
    file,
    previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    key: `${Date.now()}-${seq++}`,
  }));
}

/**
 * Append new files to an existing list, respecting the cap. Returns the new
 * list (capped) and revokes preview URLs for any files dropped by the cap.
 */
export function appendFiles(existing: PickedFile[], incoming: File[]): PickedFile[] {
  const added = toPickedFiles(incoming);
  const combined = [...existing, ...added];
  if (combined.length <= QC_MAX_ATTACHMENTS) return combined;
  // Over the cap — keep the first QC_MAX_ATTACHMENTS, revoke the rest's previews.
  for (const dropped of combined.slice(QC_MAX_ATTACHMENTS)) {
    if (dropped.previewUrl) URL.revokeObjectURL(dropped.previewUrl);
  }
  return combined.slice(0, QC_MAX_ATTACHMENTS);
}

interface Props {
  files: PickedFile[];
  setFiles: (files: PickedFile[]) => void;
  disabled?: boolean;
}

export function AttachmentPicker({ files, setFiles, disabled }: Props) {
  // Revoke any outstanding preview URLs when the picker unmounts.
  useEffect(
    () => () => {
      for (const f of files) if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) setFiles(appendFiles(files, picked));
    e.target.value = ''; // allow re-picking the same file after a remove
  }

  function remove(key: string) {
    const target = files.find((f) => f.key === key);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    setFiles(files.filter((f) => f.key !== key));
  }

  const atCap = files.length >= QC_MAX_ATTACHMENTS;
  const inputRef = useRef<HTMLInputElement>(null);

  // The browser's native "Choose Files | No file chosen" control was the one
  // raw-looking element on the reply composer. The input stays (it IS the
  // picker) but hidden; a normal WISE button opens it.
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={labelStyle}>Attachments</span>
        {!atCap && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              disabled={disabled}
              onChange={onInputChange}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              style={{ ...secondaryButtonStyle, fontSize: 12, padding: '4px 10px' }}
            >
              📎 Add files
            </button>
          </>
        )}
        <span style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>
          or paste a screenshot — screenshots make bugs much faster to fix
        </span>
      </div>

      {files.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {files.map((f) => (
            <div
              key={f.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                border: '1px solid var(--brand-border, #D6E4EE)',
                borderRadius: 6,
                padding: '8px 12px',
                background: 'var(--brand-bg, #F4F8FB)',
              }}
            >
              {f.previewUrl && (
                <img
                  src={f.previewUrl}
                  alt="attachment preview"
                  style={{
                    height: 48,
                    width: 72,
                    objectFit: 'cover',
                    borderRadius: 4,
                    border: '1px solid var(--brand-border, #D6E4EE)',
                  }}
                />
              )}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  color: 'var(--brand-text, #001C3E)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {f.file.name}
                <span style={{ color: 'var(--brand-text-muted, #8BA4B8)', marginLeft: 8 }}>
                  {(f.file.size / 1024).toFixed(0)} KB
                </span>
              </div>
              <button
                type="button"
                onClick={() => remove(f.key)}
                disabled={disabled}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--brand-text-muted, #8BA4B8)',
                  cursor: 'pointer',
                  fontSize: 12,
                  textDecoration: 'underline',
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {atCap && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>
          Maximum of {QC_MAX_ATTACHMENTS} attachments reached.
        </div>
      )}
    </div>
  );
}
